import { Mail, Phone, User, IdCard } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";

interface ProfileDialogProps {
  open: boolean;
  onClose: () => void;
}

function DetailRow({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: string }) {
  return (
    <div className="flex items-center gap-3">
      <Icon className="h-5 w-5 text-muted-foreground shrink-0" />
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-medium">{value}</p>
      </div>
    </div>
  );
}

export default function ProfileDialog({ open, onClose }: ProfileDialogProps) {
  const { user } = useAuth();

  if (!user) return null;

  const initials = user.fullname
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <Dialog open={open} onOpenChange={(val) => { if (!val) onClose(); }}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Profile</DialogTitle>
        </DialogHeader>

        {/* Avatar + Name */}
        <div className="flex flex-col items-center pt-1 pb-4">
          <Avatar className="h-20 w-20 mb-3">
            <AvatarImage src={user.dp || undefined} />
            <AvatarFallback className="bg-primary text-white text-xl">
              {initials}
            </AvatarFallback>
          </Avatar>
          <h3 className="text-lg font-bold">{user.fullname}</h3>
          {user.username && (
            <Badge variant="outline" className="mt-1">
              @{user.username}
            </Badge>
          )}
        </div>

        <Separator />

        {/* Details */}
        <div className="flex flex-col gap-4 pt-2">
          <DetailRow icon={Mail} label="Email" value={user.email} />
          <DetailRow icon={Phone} label="Phone" value={user.phone} />
          {user.username && (
            <DetailRow icon={User} label="Username" value={user.username} />
          )}
          <div className="flex items-center gap-3">
            <IdCard className="h-5 w-5 text-muted-foreground shrink-0" />
            <div>
              <p className="text-xs text-muted-foreground">User ID</p>
              <p className="text-xs font-medium font-mono">{user.id}</p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
